/**
 * v0.5.0 settings section: sessions the user (or the daemon) stopped monitoring.
 *
 * Display rules (2026-10-19 user request):
 * - 「我手动停止的」: collapsible; only manual restore.
 * - 「自动停止的（无法读取）」: collapsible; only corrupt auto entries.
 *   Sessions auto-excluded for 7-day inactivity are **completely hidden** from
 *   the app (not in the session list and not listed here); the daemon still
 *   filters them out. The list is collapsible because it can be long.
 *
 * Each action is a signed `unignore` command reconciled from
 * `session.command.result` (see AppState); the server exclusion list is
 * authoritative for non-pending entries.
 */
import { useState } from 'react';
import { formatRelative } from '../lib/format';
import { nowSeconds } from '../lib/id';
import {
  SESSION_MONITORING_AUTO_CORRUPT_COPY,
  SESSION_MONITORING_EMPTY_MANUAL_COPY,
  SESSION_MONITORING_LARGE_SESSION_COPY,
  SESSION_MONITORING_PENDING_COPY,
  SESSION_MONITORING_SECTION_HINT,
  displaySessionExclusions,
  sessionExclusionKey,
  sessionExclusionReasonLabel,
  sessionExclusionResumeLabel,
  sessionExclusionTitle,
  type SessionExclusionEntry,
} from '../lib/session-monitoring';
import { useApp } from '../state/AppState';

function EntryRow({
  entry,
  busy,
  disabled,
  onResume,
}: {
  entry: SessionExclusionEntry;
  busy: boolean;
  disabled: boolean;
  onResume(entry: SessionExclusionEntry): void;
}) {
  const now = nowSeconds();
  const pending = entry.pending === true;
  const actionLabel = sessionExclusionResumeLabel(entry);
  return (
    <li
      data-testid="session-monitoring-entry"
      data-key={sessionExclusionKey(entry.source, entry.session_id)}
      className="rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2.5"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="break-words text-xs text-slate-200">{sessionExclusionTitle(entry)}</p>
          <p className="mt-0.5 break-all font-mono text-[10px] text-slate-500">
            {entry.source} / {entry.session_id}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-slate-500">
            <span
              className={`rounded-full border px-1.5 py-0.5 ${
                entry.mode === 'auto'
                  ? 'border-amber-400/30 bg-amber-400/10 text-amber-200'
                  : 'border-slate-500/30 bg-slate-500/10 text-slate-300'
              }`}
            >
              {sessionExclusionReasonLabel(entry)}
            </span>
            <span>更新于 {formatRelative(entry.updated_at, now)}</span>
            {pending ? (
              <span className="text-amber-300">{SESSION_MONITORING_PENDING_COPY}</span>
            ) : null}
          </p>
        </div>
        <button
          type="button"
          data-testid={`session-monitoring-resume-${sessionExclusionKey(entry.source, entry.session_id)}`}
          disabled={busy || disabled || pending}
          onClick={() => onResume(entry)}
          className="shrink-0 rounded-lg border border-indigo-400/30 bg-indigo-400/10 px-2.5 py-1.5 text-[11px] text-indigo-100 disabled:opacity-40"
        >
          {busy ? '发送中…' : actionLabel}
        </button>
      </div>
      {entry.last_error ? (
        <p className="mt-1.5 break-words rounded-lg border border-rose-400/25 bg-rose-400/10 px-2 py-1 text-[10px] leading-relaxed text-rose-200">
          {entry.last_error}
        </p>
      ) : null}
    </li>
  );
}

function Header({ title, count }: { title: string; count: number }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <h3 className="text-xs font-medium text-slate-200">{title}</h3>
      <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-400">
        {count}
      </span>
    </span>
  );
}

function SubSection({
  id,
  title,
  count,
  emptyCopy,
  entries,
  busyKey,
  onResume,
}: {
  id: 'manual' | 'auto';
  title: string;
  count: number;
  emptyCopy: string;
  entries: readonly SessionExclusionEntry[];
  busyKey: string | null;
  onResume(entry: SessionExclusionEntry): void;
}) {
  const [open, setOpen] = useState(false);
  if (entries.length === 0) {
    return (
      <div data-testid={`session-monitoring-section-${id}`}>
        <Header title={title} count={0} />
        <p className="mt-2 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 text-[11px] leading-relaxed text-slate-500">
          {emptyCopy}
        </p>
      </div>
    );
  }
  return (
    <div data-testid={`session-monitoring-section-${id}`} data-open={open ? 'true' : 'false'}>
      <details
        open={open}
        onToggle={(event) => setOpen((event.currentTarget as HTMLDetailsElement).open)}
        className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2"
      >
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
          <Header title={title} count={count} />
          <span className="shrink-0 text-[10px] text-slate-500">{open ? '收起' : '展开'}</span>
        </summary>
        <ul className="mt-2 max-h-80 space-y-2 overflow-y-auto pr-1">
          {entries.map((entry) => {
            const key = sessionExclusionKey(entry.source, entry.session_id);
            return (
              <EntryRow
                key={key}
                entry={entry}
                busy={busyKey === key}
                disabled={busyKey !== null}
                onResume={onResume}
              />
            );
          })}
        </ul>
      </details>
    </div>
  );
}

export function SessionMonitoringSection() {
  const {
    sessionExclusions,
    sessionMonitoringNotice,
    dismissSessionMonitoringNotice,
    unignoreSession,
  } = useApp();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const { manual, auto } = displaySessionExclusions(sessionExclusions);

  const resume = async (entry: SessionExclusionEntry): Promise<void> => {
    if (busyKey !== null) return;
    setBusyKey(sessionExclusionKey(entry.source, entry.session_id));
    try {
      await unignoreSession(entry);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <section
      data-testid="session-monitoring-settings"
      className="rounded-2xl border border-white/10 bg-[#0f1629] p-4"
    >
      <h2 className="text-sm font-semibold text-slate-100">已停止监视的会话</h2>
      <p className="mt-0.5 text-[11px] text-slate-500">{SESSION_MONITORING_SECTION_HINT}</p>

      {sessionMonitoringNotice ? (
        <div
          data-testid="session-monitoring-settings-notice"
          className={`mt-3 flex items-start justify-between gap-2 rounded-xl border px-3 py-2 text-[11px] leading-relaxed ${
            sessionMonitoringNotice.tone === 'success'
              ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
              : sessionMonitoringNotice.tone === 'error'
                ? 'border-rose-400/30 bg-rose-400/10 text-rose-200'
                : 'border-amber-400/30 bg-amber-400/10 text-amber-200'
          }`}
        >
          <span className="min-w-0 break-words">{sessionMonitoringNotice.message}</span>
          <button
            type="button"
            onClick={dismissSessionMonitoringNotice}
            aria-label="关闭监视状态提示"
            className="shrink-0 rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-300"
          >
            关闭
          </button>
        </div>
      ) : null}

      <div className="mt-3 space-y-3">
        <SubSection
          id="manual"
          title="我手动停止的"
          count={manual.length}
          emptyCopy={SESSION_MONITORING_EMPTY_MANUAL_COPY}
          entries={manual}
          busyKey={busyKey}
          onResume={(entry) => void resume(entry)}
        />
        {auto.length > 0 ? (
          <SubSection
            id="auto"
            title="自动停止的（无法读取）"
            count={auto.length}
            emptyCopy=""
            entries={auto}
            busyKey={busyKey}
            onResume={(entry) => void resume(entry)}
          />
        ) : null}
      </div>

      <ul className="mt-3 list-disc space-y-1 pl-4 text-[10px] leading-relaxed text-slate-500">
        <li>7 天无活动的会话已自动停止监视，并从会话列表和此处完全隐藏（不会再显示）。</li>
        <li>{SESSION_MONITORING_AUTO_CORRUPT_COPY}</li>
        <li>{SESSION_MONITORING_LARGE_SESSION_COPY}</li>
        <li>手动停止的条目只能手动恢复；无法读取的条目可重试读取。</li>
      </ul>
    </section>
  );
}
